/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import React, { useRef, useEffect } from 'react';
import Glider from 'react-glider';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import Skeleton from 'react-loading-skeleton';

// app: components
import BookShelfTile from 'components/BookShelf/BookShelfTile';
import EmptyShelf from 'components/EmptyShelf';
import Heading from 'components/Heading';
import Link from 'components/Link';

// app: services
import { isApp } from 'services/environment';
import formatter from 'services/formatter';
import { booksUrl } from 'services/route';

// assets
import CssGlider from 'glider-js/glider.min.css';
import CssBookShelf from './BookShelf.scss';

// app: queries
import GetBookShelfQuery from './GetBookShelf.gql';

function BookShelf(props) {
  const {
    backText,
    featured,
    filters,
    headingLink,
    id,
    includeAlacarte,
    initialScrollPos,
    itemCount,
    itemWidth,
    load,
    maxItems,
    moreLink,
    onItemClick,
    orderBy,
    orderByDirection,
    preloadItems,
    query,
    showItemCountThreshold,
    ssr,
    title,
    titleAuthors,
    useArrows,
    userSpecific,
  } = props;
  useStyles(CssBookShelf, CssGlider);
  const gliderRef = useRef();

  const includeTags = useSelector(state =>
    state.user.oven_brands?.map(brand => brand.toLowerCase()),
  );
  const country = useSelector(state => state.user.geolocation.countryCode);

  useEffect(() => {
    if (initialScrollPos && gliderRef.current) {
      gliderRef.current.scrollTo(initialScrollPos, 0);
    }
  });

  const { previousData, data = previousData, loading, error } = useQuery(GetBookShelfQuery, {
    variables: {
      _authenticated: userSpecific,
      filters,
      includeAlacarte,
      includeTags,
      limit: maxItems,
      offset: 0,
      orderBy,
      orderByDirection,
      query,
      country,
    },
    fetchPolicy: isApp() || userSpecific ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    skip: load === false,
    ssr,
  });
  const hasMore = data?.Books?.length >= maxItems;

  const loadImage = index => {
    if (gliderRef.current && index >= 0) {
      const slideEl = gliderRef.current.slides[index];
      if (slideEl && !slideEl.dataset.loadTriggered) {
        slideEl.dataset.loadTriggered = true;
        if (!(hasMore && index === gliderRef.current.slides.length - 1)) {
          // This is a normal slide with a book cover image in it.
          Array.from(slideEl.getElementsByTagName('img')).forEach(img => {
            img.setAttribute('src', img.dataset.src);
            img.setAttribute('srcset', img.dataset.srcSet);
            img.removeAttribute('data-src');
            img.removeAttribute('data-src-set');
          });
        }
      }
    }
  };

  const handleSlideVisible = event => {
    const { slide } = event.detail;
    for (let i = 0; i < 3; i += 1) {
      loadImage(slide + i, true);
    }
  };

  const handleSlideClick = (event, itemIndex) => {
    if (gliderRef.current) {
      const scrollPosition = gliderRef.current.ele.scrollLeft;
      onItemClick({ shelfId: id, title, scrollPosition, itemIndex });
    }
  };

  const itemCountText = itemCount >= showItemCountThreshold ? `(${itemCount})` : null;
  let titleContent;
  if (titleAuthors?.length) {
    titleContent = formatter.authorsList.toDOMWithLinks(titleAuthors, personId =>
      booksUrl({
        tab: 'authors',
        category: personId,
      }),
    );
  } else if (moreLink && title) {
    titleContent = (
      <Link
        to={moreLink}
        onClick={event => handleSlideClick(event, data?.Books?.length)}
        referrer={backText}
      >
        {title} {itemCountText}
      </Link>
    );
  } else {
    titleContent = title || <Skeleton width={120} />;
  }
  return (
    <div className="book-shelf">
      <header>
        <Heading withDash className="margin-right-10">
          {titleContent}
        </Heading>
        {featured ? (
          <p className="margin-right-10 margin-bottom-5 book-shelf__featured">Featured</p>
        ) : null}
        {headingLink ? (
          <p className="margin-bottom-5">
            {headingLink.href ? (
              <Link to={headingLink.href} className="link--underline">
                {headingLink.text}
              </Link>
            ) : (
              headingLink.text
            )}
          </p>
        ) : null}
      </header>
      {(data?.Books?.length === 0 || error) && !loading ? (
        <EmptyShelf height={itemWidth} userSpecific={userSpecific} />
      ) : (
        <Glider
          className="book-shelf__glider"
          draggable
          exactWidth
          hasArrows={useArrows}
          iconLeft={
            <NavigateBeforeIcon
              fontSize="large"
              className="book-shelf__arrow book-shelf__arrow__left"
            />
          }
          iconRight={
            <NavigateNextIcon
              fontSize="large"
              className="book-shelf__arrow book-shelf__arrow__right"
            />
          }
          itemWidth={itemWidth}
          onSlideVisible={handleSlideVisible}
          ref={gliderRef}
          resizeLock={false} // iOS Safari triggers resize events on scroll. Switch this off to avoid glitches
          skipTrack
          slidesToScroll="auto"
          slidesToShow="auto"
        >
          <div className="glider-track book-shelf__track">
            {data && data.Books
              ? data.Books.slice(0, maxItems - 3).map((book, index) => (
                  <BookShelfTile
                    backText={backText}
                    className="glider-slide book-shelf__slide"
                    key={book.isbn}
                    lazy={index > preloadItems}
                    width={itemWidth}
                    height={itemWidth}
                    onClick={event => handleSlideClick(event, index)}
                    {...book}
                  />
                ))
              : [...Array(preloadItems)].map((e, index) => (
                  <BookShelfTile
                    backText={backText}
                    className="glider-slide book-shelf__slide"
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    lazy
                    width={itemWidth}
                    height={itemWidth}
                  />
                ))}
            {hasMore && moreLink ? (
              <div className="glider-slide book-shelf__slide book-shelf__more-slide">
                <Link
                  to={moreLink}
                  onClick={event => handleSlideClick(event, data?.Books?.length)}
                  className="book-shelf__more"
                  referrer={backText}
                >
                  <Heading level={2} className="heading heading--size-64">
                    View All
                  </Heading>
                </Link>
              </div>
            ) : null}
          </div>
        </Glider>
      )}
    </div>
  );
}

BookShelf.propTypes = {
  backText: PropTypes.string,
  featured: PropTypes.bool,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  headingLink: PropTypes.shape({
    href: PropTypes.string,
    text: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  includeAlacarte: PropTypes.bool,
  initialScrollPos: PropTypes.number,
  itemCount: PropTypes.number,
  itemWidth: PropTypes.number,
  load: PropTypes.bool,
  maxItems: PropTypes.number,
  moreLink: PropTypes.string,
  onItemClick: PropTypes.func,
  orderBy: PropTypes.string,
  orderByDirection: PropTypes.string,
  preloadItems: PropTypes.number,
  query: PropTypes.string,
  showItemCountThreshold: PropTypes.number,
  ssr: PropTypes.bool,
  title: PropTypes.string,
  titleAuthors: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    is_author: PropTypes.string,
  }),
  useArrows: PropTypes.bool,
  userSpecific: PropTypes.bool,
};

BookShelf.defaultProps = {
  backText: null,
  featured: false,
  filters: [],
  headingLink: null,
  id: null,
  includeAlacarte: false,
  initialScrollPos: 0,
  itemCount: null,
  itemWidth: 250,
  load: true,
  maxItems: 25,
  moreLink: null,
  onItemClick: () => {},
  orderBy: 'rank',
  orderByDirection: 'desc',
  preloadItems: 8,
  query: null,
  showItemCountThreshold: 5,
  ssr: true,
  title: null,
  titleAuthors: null,
  useArrows: true,
  userSpecific: false,
};

export default React.memo(BookShelf);
